import PropTypes from "prop-types";
import styles from "./FilterMenu.module.css";
import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Close } from "react-ikonate";

import searchIcon from "./Search_Icon.svg";

import SearchBar from "../SearchBar";
import Modal from "components/Modal";
import DropdownButton from "../DropdownButton";
import FilterButton from "components/FilterButton";

/**
 * Filter bar: shows two dropdowns (Layout, Sort) + search icon
 * Now starts with actual selected values (no placeholders).
 */
export function FilterMenu({
  backButton = false,
  handleBackClick,
  dropdownOne, // layout options
  dropdownTwo, // sort options
  selectedOneValue, // e.g., "list"
  selectedTwoValue, // e.g., "newest"
  handlePickOne,
  handlePickTwo,
  filterButton = true,
  onSubCategoryChange,
  onCategoryChange,
  onSearchInput,
  apiCategories = [],
  initialCategory = "",
  initialSubCategory = "",
  initialSearchTerm = "",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);

  // Local selected option objects so we can render their labels
  const [selectedOne, setSelectedOne] = useState(null);
  const [selectedTwo, setSelectedTwo] = useState(null);
  const [isUserSelectedTwo, setIsUserSelectedTwo] = useState(true);

  // Initialize from provided values on mount and when lists change
  useEffect(() => {
    const initOne =
      dropdownOne?.find((o) => o.value === selectedOneValue) ||
      dropdownOne?.[0] ||
      null;
    const initTwo =
      dropdownTwo?.find((o) => o.value === selectedTwoValue) ||
      dropdownTwo?.[0] ||
      null;

    if (initOne) {
      setSelectedOne(initOne);
      handlePickOne?.(initOne.value);
    }
    if (initTwo) {
      setSelectedTwo(initTwo);
      handlePickTwo?.(initTwo.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownOne, dropdownTwo]);

  const handleSearchClick = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);
  const handleCancel = () => {
    setIsOpen(false);
    setSearchTerm("");
    onSearchInput?.("");
  };

  const handleSearchInput = useCallback(
    (term) => {
      setSearchTerm(term ?? "");
      onSearchInput?.(term);
    },
    [onSearchInput]
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    onSearchInput?.("");
  }, [onSearchInput]);

  const hasSearchTerm = searchTerm.trim() !== "";

  // Keep local & parent in sync
  const onPickOne = (value) => {
    const opt = dropdownOne?.find((o) => o.value === value);
    if (opt) setSelectedOne(opt);
    handlePickOne?.(value);
  };
  const onPickTwo = (value) => {
    const opt = dropdownTwo?.find((o) => o.value === value);
    if (opt) {
      setSelectedTwo(opt);
      setIsUserSelectedTwo(true);
    }
    handlePickTwo?.(value);
  };

  return (
    <>
      <div className={styles.container}>
        <Modal
          isOpen={isOpen}
          onClose={handleClose}
          centered={false}
          className={styles.background}
          children={
            <motion.div
              className={styles.searchReveal}
              initial={{ width: "8%", opacity: 0 }}
              animate={{ width: "100%", opacity: 1 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <SearchBar
                placeholder={"Search"}
                autoFocus
                enterKeyHint="search"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                value={searchTerm}
                onClick={handleCancel}
                onInput={handleSearchInput}
                onSubmit={handleClose}
              />
            </motion.div>
          }
        />

        <div className={styles.btnList}>
          <DropdownButton
            // Show the real selected label (so it's not a placeholder)
            placeholder={selectedOne?.label || ""}
            options={dropdownOne}
            handlePick={onPickOne}
            selectedOption={selectedOne}
          />
        </div>

        <div className={styles.btnSort}>
          <DropdownButton
            // Show "Sort" if user hasn't selected anything, otherwise show actual label
            placeholder={isUserSelectedTwo ? selectedTwo?.label || "" : "Sort"}
            options={dropdownTwo}
            handlePick={onPickTwo}
            selectedOption={isUserSelectedTwo ? selectedTwo : null}
          />
        </div>

        <div className={styles.searchIcon}>
          {hasSearchTerm ? (
            <Close
              className={styles.clearIcon}
              onClick={handleClearSearch}
              role="button"
              aria-label="Clear search"
            />
          ) : (
            <img
              src={searchIcon}
              alt="Search Icon"
              onClick={handleSearchClick}
            />
          )}
        </div>
      </div>

      {filterButton && (
        <div className={styles.container}>
          <div className={styles.filterButtons}>
            <FilterButton
              onSubCategoryChange={onSubCategoryChange}
              onCategoryChange={onCategoryChange}
              apiCategories={apiCategories}
              initialCategory={initialCategory}
              initialSubCategory={initialSubCategory}
            />
          </div>
        </div>
      )}
    </>
  );
}

FilterMenu.propTypes = {
  dropdownOne: PropTypes.array.isRequired,
  dropdownTwo: PropTypes.array.isRequired,
  selectedOneValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedTwoValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handlePickOne: PropTypes.func.isRequired,
  handlePickTwo: PropTypes.func.isRequired,
  backButton: PropTypes.bool,
  handleBackClick: PropTypes.func,
  filterButton: PropTypes.bool,
  onSubCategoryChange: PropTypes.func,
  onCategoryChange: PropTypes.func,
  onSearchInput: PropTypes.func,
  apiCategories: PropTypes.array,
  initialCategory: PropTypes.string,
  initialSubCategory: PropTypes.string,
  initialSearchTerm: PropTypes.string,
};
